/**
 * @typedef {Object} Gateway
 * @property {string} general.id
 * @property {string} general.ownerId
 * @property {string} general.planId
 * ---- General ----
 * @property {object} general
 * @property {string} general.description
 * @property {string} general.enabled
 * @property {string} general.gatewayType
 * @property {string} general.reportedFirmware
 * @property {string} general.managedBySmp
 * @property {string} general.macAddress
 * @property {string} general.lastConnectedIpAddress
 * @property {string} general.registrationKey
 * @property {string} general.activationKey
 * ---- Location ----
 * @property {object} location
 * @property {string} location.description
 * ---- Owner ----
 * @property {object} owner
 * @property {string} owner.company
 * @property {string} owner.email
 * @property {string} owner.firstName
 * @property {string} owner.lastName
 * @property {string} owner.userId
 */

export const GatewayGetSchema = {
    id: 'id',
    ownerId: 'ownerId',
    planId: 'planId',
    isClusterGateway: 'isClusterGateway',
    memberOfCluster: 'memberOfCluster',
    pages: {
        general: {
            pageId: 'general',
            description: 'description',
            enabled: 'enabled',
            gatewayType: 'gatewayType',
            reportedFirmware: 'reportedFirmware',
            managedBySmp: 'managedBySmp',
            macAddress: 'macAddress',
            lastConnectedIp: 'lastConnectedIpAddress',
            registrationKey: 'registrationKey',
            activationKey: 'activationKey',
            staticIp: 'staticIp'
        },
        location: {
            pageId: 'location',
            lat: 'lat',
            lng: 'lng'
        },
        owner: {
            pageId: 'owner'
        },
        status: {
            pageId: 'status',
            created: 'created',
            connected: 'connected',
            connectionStatus: 'connectionStatus',
            currentServer: 'currentServer',
            lastConnectedIpAddress: 'lastConnectedIpAddress',
            lastConnectionDay: 'lastConnectionDay',
            lastFetchTime: 'lastFetchTime',
            lastModified: 'lastModified',
            lastSyncStatus: 'lastSyncStatus',
            lastUpdateTime: 'lastUpdateTime',
            reportedFirmware: 'reportedFirmware'
        },
        internetMonitoring: {
            pageId: 'internetMonitoring',
            internetProbingServers: 'internetProbingServers',
            internetProbingServersSchema: {
                name: 'name',
                connName: 'connName',
                ipAdd: 'ipAdd',
                packetLoss: 'packetLoss',
                failures: 'failures',
                avgLatency: 'avgLatency',
                minLatency: 'minLatency',
                maxLatency: 'maxLatency',
                jitter: 'jitter',
            }
        },
        ntp: {
            pageId: 'ntp',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            ntpAuthentication: 'sharedSecretStatus',
            primaryNtpServer: 'primaryServer',
            secondaryNtpServer: 'secondaryServer',
            sharedSecret: 'authKeyID',
            sharedSecretIdentifier: 'authKeyValue',
            updateIntervalMinutes: 'reSyncNtpVal',
        },
        timezone: {
            pageId: 'timeZone',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            localTimeZone: 'localtimezone',
            autoDaylightSavings: 'autoDaylightSavings'
        },
        dns: {
            pageId: 'dns',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            configureDnsServers: 'serversConfiguration',
            configureDnsServersValues: {
                GLOBAL: 'GLOBAL',
                INTERNET: 'INTERNET'
            },
            domainName: 'dnsDomainName',
            enableDnsProxy: 'enableDnsProxy',
            resolveNetworkObjects: 'resolveNetworkObjects',
            firstDnsServer: 'globalDnsServer1',
            secondDnsServer: 'globalDnsServer2',
            thirdDnsServer: 'globalDnsServer3'
        },
        administrators: {
            pageId: 'administrators',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            administrators: 'administrators',
            administratorsSchema: {
                username: 'username',
                password: 'password',
                role: 'role'
            }
        },
        localUsers: {
            pageId: 'localUsers',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            localUsers: 'localUsers',
            localUsersSchema: {
                username: "username",
                password: "password",
                email: "email",
                phoneNumber: "phoneNumber",
                comments: "comments",
                isRemoteAccessUser: "isRemoteAccessUser"
            }
        },
        firewall: {
            pageId: 'firewall',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            policy: 'policy',
            logAllowedTraffic: 'logAllowedTraffic',
            logAllowedTrafficValue: 'logAllowedTrafficValue',
            logBlockedTraffic: 'logBlockedTraffic',
            logBlockedTrafficValue: 'logBlockedTrafficValue',
        },
        accessPolicy: {
            pageId: 'accessPolicy',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            ruleBaseType: 'ruleBaseType',
            ruleBaseTypeValues: {
                LEGACY: 'LEGACY',
                NEXT_GEN: 'NEXT_GEN',
            },
            legacyRules: 'legacyRules',
            nextGenRules: 'nextGenRules',
            cpAccessRulesSchema: {
                preOutgoingRules: 'preOutgoingRules',
                postOutgoingRules: 'postOutgoingRules',
                preIncomingRules: 'preIncomingRules',
                postIncomingRules: 'postIncomingRules',
            },
            cpRuleSchema: {
                source: 'source',
                destination: 'destination',
                sourceDestinationSchema: {
                    type: 'type',
                    zone: 'zone',
                    ipSingle: 'ipSingle',
                    ipStart: 'ipStart',
                    ipEnd: 'ipEnd',
                    ipNetwork: 'ipNetwork',
                    ipMask: 'ipMask',
                    schemaIpRange: 'schemaIpRange',
                    schemaIpRangeSchema: {
                        ipStart: 'ipStart',
                        ipEnd: 'ipEnd',
                    },
                    schemaNetwork: 'schemaNetwork',
                    schemaNetworkSchema: {
                        ipNetwork: 'ipNetwork',
                        ipMask: 'ipMask'
                    },
                    ipV6Single: 'ipV6Single',
                    ipV6Start: 'ipV6Start',
                    ipV6End: 'ipV6End',
                    ipV6Network: 'ipV6Network',
                    ipV6Prefix: 'ipV6Prefix',
                    ipV6ObjectName: 'ipV6ObjectName',
                    schemaIpV6Range: 'schemaIpV6Range',
                    schemaIpV6RangeSchema: {
                        ipV6Start: 'ipV6Start',
                        ipV6End: 'ipV6End',
                    },
                    schemaIpV6Network: 'schemaIpV6Network',
                    schemaIpV6NetworkSchema: {
                        ipIpV6Network: 'ipV6Network',
                        ipV6Prefix: 'ipV6Prefix'
                    }
                },
                serviceOrApplication: 'serviceOrApplication',
                serviceOrApplicationValues: {
                    SERVICE: 'SERVICE',
                    APPLI: 'APPI',
                },
                service: 'service',
                serviceSchema: {
                    type: "type",
                    customType: "customType",
                    name: "name",
                    ports: "ports",
                    icmpType: "icmpType",
                    icmpCode: "icmpCode",
                    ipProtocol: "ipProtocol"
                },
                application: 'application',
                applicationSchema: {
                    type: "type",
                    predefinedAppId: "predefinedAppId",
                    customUrlValue: "customUrlValue",
                    customUrlName: "customUrlName",
                    customUrlIsRegex: "customUrlIsRegex",
                },
                action: 'action',
                log: 'log',
                description: 'description',
                enabled: 'enabled'
            }
        },
        applicationsAndUrls: {
            pageId: 'applicationsAndUrls',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            blockSecurityRiskCategories: 'blockSecurityRiskCategories',
            blockFileSharingApplications: 'blockFileSharingApplications',
            blockInappropriateContent: 'blockInappropriateContent',
            blockOtherUndesiredApplications: 'blockOtherUndesiredApplications',
            appiBlockList: 'appiBlockList'
        },
        ips: {
            pageId: 'ips',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            policy: 'policy',
            policyValues: {
                strict: 'STRICT',
                standard: 'STANDARD',
                custom: 'CUSTOM'
            },
            detectOnlyMode: 'detectOnlyMode',
        },
        traditionalAntiVirus: {
            pageId: 'traditionalAntiVirus',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            scanIncoming: 'scanIncoming',
            scanOutgoing: 'scanOutgoing',
            scanInternal: 'scanInternal',
            scanIncomingHttp: 'scanIncomingHttp',
            scanIncomingSmtp: 'scanIncomingSmtp',
            scanIncomingFtp: 'scanIncomingFtp',
            scanOutgoingHttp: 'scanOutgoingHttp',
            scanOutgoingSmtp: 'scanOutgoingSmtp',
            scanOutgoingFtp: 'scanOutgoingFtp',
            scanInternalHttp: 'scanInternalHttp',
            scanInternalSmtp: 'scanInternalSmtp',
            scanInternalFtp: 'scanInternalFtp',
            track: 'track',
            trackValues: {
                log: 'LOG',
                alert: 'ALERT',
                none: 'NONE'
            },
            detectOnly: 'detectOnly',
            networks: 'networks',
            networkValues: {
                allInternal: 'ALL_INTERNAL',
                dmzToInternal: 'DMZ_TO_INTERNAL'
            }
        },
        antiSpam: {
            pageId: 'antiSpam',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            contentBasedFilter: 'contentBasedFilter',
            spamAction: 'spamAction',
            spamActionValues: {
                blockSpamEmails: 'BLOCK_SPAM_EMAILS',
                flagSpamEmailSubject: 'FLAG_SPAM_EMAIL_SUBJECT',
                flagSpamEmailHeader: 'FLAG_SPAM_EMAIL_HEADER',
            },
            track: 'track',
            trackValues: {
                log: 'LOG',
                alert: 'ALERT',
                none: 'NONE'
            },
            detectOnly: 'detectOnly',
            spamEmailSubject: 'spamEmailSubject'
        },
        qos: {
            pageId: 'qos',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            lowLatency: 'lowLatency',
            guarantee: 'guarantee',
            guaranteePercentage: 'guaranteePercentage',
            guaranteeTraffic: 'guaranteeTraffic',
            guaranteeTrafficValues: {
                vpnTraffic: 'VPN_TRAFFIC',
                allTraffic: 'ALL_TRAFFIC'
            },
            limitApps: 'limitApps',
            limitAppsDownload: 'limitAppsDownload',
            limitAppsUpload: 'limitAppsUpload',
            limitDownloadSize: 'limitDownloadSize',
            limitUploadSize: 'limitUploadSize',
        },
        remoteAccess: {
            pageId: 'remoteAccess',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            allowTraffic: 'allowTraffic',
            logTraffic: 'logTraffic',
        },
        siteToSiteVpn: {
            pageId: 'siteToSiteVPN',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            allowTraffic: 'allowTraffic',
            logTraffic: 'logTraffic'
        },
        userAwareness: {
            pageId: 'userAwareness',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode'
        },
        antiVirus: {
            pageId: 'antiVirus',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            detect: 'detect'
        },
        antiBot: {
            pageId: 'antiBot',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            detect: 'detect',
            inspectSize: 'inspectSize'
        },
        threatEmulation: {
            pageId: 'threatEmulation',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            detect: 'detect',
            connectionHandlingModeHttp: 'connectionHandlingModeHttp',
            connectionHandlingModeHttpValues: {
                background: 'TE_CONNECTION_HANDLING_MODE.BACKGROUND',
                hold: 'TE_CONNECTION_HANDLING_MODE.HOLD'
            }
        },
        threatPrevention: {
            pageId: 'threatPrevention',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            highConfidence: 'highConfidence',
            mediumConfidence: 'mediumConfidence',
            lowConfidence: 'lowConfidence',
            confidenceValues: {
                ask: 'ASK',
                prevent: 'PREVENT',
                detect: 'DETECT',
                inactive: 'INACTIVE'
            },
            performanceImpact: 'performanceImpact',
            performanceImpactValues: {
                low: 'LOW',
                medium: 'MEDIUM',
                high: 'HIGH'
            },
            track: 'track',
            trackValues: {
                log: 'LOG',
                none: 'NONE',
                alert: 'ALERT'
            }
        },
        unifiedThreatPrevention: {
            pageId: 'unifiedThreatPrevention',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            highConfidence: 'highConfidence',
            mediumConfidence: 'mediumConfidence',
            lowConfidence: 'lowConfidence',
            confidenceValues: {
                ask: 'ASK',
                prevent: 'PREVENT',
                detect: 'DETECT',
                inactive: 'INACTIVE'
            },
            performanceImpact: 'performanceImpact',
            performanceImpactValues: {
                low: 'LOW',
                medium: 'MEDIUM',
                high: 'HIGH'
            },
            track: 'track',
            trackValues: {
                log: 'LOG',
                none: 'NONE',
                alert: 'ALERT'
            },
            policy: 'policy',
            policyValues: {
                strict: 'STRICT',
                recommended: 'RECOMMENDED',
                custom: 'CUSTOM'
            },
            severity: 'severity',
            severityValues: {
                low: 'LOW',
                medium: 'MEDIUM',
                high: 'HIGH',
                critical: 'CRITICAL'
            }
        },
        threatPreventionExceptions: {
            pageId: 'threatPreventionExceptions',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            whitelistUrls: 'allowlistUrls',
            whiteListUrlSchema: {
                value: 'value',
            },
        },
        sslInspection: {
            pageId: 'sslInspection',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            mode: 'mode',
            policy: 'policy',
            policySchema: {
                sslTrafficInspection: 'SSL_INSPECTION',
                httpsCategorization: 'HTTPS_CATEGORIZATION',

            },
            inspectHttps: 'inspectHttps',
            inspectImaps: 'inspectImaps',
            trustedNetworks: 'trustedNetworks',
            untrustedNetworks: 'untrustedNetworks',
            healthCategory: 'healthCategory',
            governmentCategory: 'governmentCategory',
            financeServiceCategory: 'financeServiceCategory',
            mediaCategory: 'mediaCategory',
            updateCategory: 'updateCategory',
            bypassOtherCategory: 'bypassOtherCategory',
            sslBlockList: 'sslBlockList',
            bypassCustomSites: 'bypassCustomSites',
            customSites: 'customSites',
            CpSSLBypassCustomSiteSchema: {
                name: 'name',
                value: 'value',
                regex: 'regex',
            },
            trackInspectLogs: 'trackInspectLogs',
            trackBypassLogs: 'trackBypassLogs',
            trackWirelessBypassLogs: 'trackWirelessBypassLogs',
        },
        managedServices: {
            pageId: 'managedServices',
            lockedToPlan: 'lockedToPlan',
            logs: 'logs',
            reports: 'reports',
            firmwareUpgrade: 'firmwareUpgrade',
            autoBackup: 'autoBackup',
            dynamicDns: 'dynDns',
            dynaminVpn: 'dynVpn',
            spp: 'spp',
            cloudNotifications: 'cloudNotifications'
        },
        firmware: {
            pageId: 'firmwareUpgrade',
            lockedToPlan: 'lockedToPlan',
            frequency: 'frequency',
            frequencyValues: {
                immediately: 'Immediately',
                daily: 'Daily',
                weekly: 'Weekly',
                monthly: 'Monthly'
            },
            time: 'time',
            weekDay: 'weekDay',
            monthDay: 'monthDay',
            mode: 'mode',
            modeValues: {
                specific: 'Specific',
                local: 'Local',
                latest: 'CpLatest'
            },
            firmware: 'firmware',
            firmware1200: 'firmware1200',
            firmwareS1S2: 'firmwareS1S2',
            firmwareV0V1: 'firmwareV0V1',
            firmwareV2V3: 'firmwareV2V3'
        },
        serviceBackup: {
            pageId: 'serviceBackup',
            lockedToPlan: 'lockedToPlan',
            protocol: 'protocol',
            backupServerPath: 'backupServerPath',
            username: 'username',
            password: 'password',
            frequency: 'frequency',
            frequencyValues: {
                daily: 'DAILY',
                weekly: 'WEEKLY',
                monthly: 'MONTHLY'
            },
            dayOfWeek: 'dayOfWeek',
            dayOfWeekValues: {
                sunday: 'SUNDAY',
                monday: 'MONDAY',
                tuesday: 'TUESDAY',
                wednesday: 'WEDNESDAY',
                thursday: 'THURSDAY',
                friday: 'FRIDAY',
                saturday: 'SATURDAY'
            },
            summaryProtocolsValues: {
                tftp: 'tftp',
                sftp: 'sftp',
                scp: 'scp',
                flash: 'flash',
            },
            dayOfMonth: 'dayOfMonth',
            backupTime: 'backupTime',
            useFileEncryption: 'useFileEncryption',
            passwordForEncrypt: 'passwordForEncrypt'
        },
        reports: {
            pageId: 'reports',
            lockedToPlan: 'lockedToPlan',
            reportsType: 'reportsType',
            reportsTypeValues: {
                gateway: 'Classic',
                smartEvent: 'Extended'
            },
            reportID: 'reportID',
            reportIDValues: 'reportIDValues',
            reportsDaily: 'reportsDaily',
            reportsWeekly: 'reportsWeekly',
            dayOfWeek: 'reportDayWeekly',
            reportsMonthly: 'reportsMonthly',
            dayOfMonth: 'reportDayMonthly',
            dayOfWeekValues: {
                sunday: 'SUNDAY',
                monday: 'MONDAY',
                tuesday: 'TUESDAY',
                wednesday: 'WEDNESDAY',
                thursday: 'THURSDAY',
                friday: 'FRIDAY',
                saturday: 'SATURDAY'
            },
            reportsLang: 'reportsLang',
            reportsLangValues: {
                english: 'ENGLISH',
                japanese: 'JAPANESE',
                hebrew: 'HEBREW',
                german: 'GERMAN'
            },
            reportAsHtmlBody: 'reportAsHtmlBody',
            reportsTimezone: 'reportsTimezone',
            reportsConfirmationEnabled: 'reportsConfirmationEnabled'
        },
        reportRecipients: {
            pageId: 'reportRecipients',
            lockedToPlan: 'lockedToPlan',
            sendToOwner: 'sendToOwner',
            additionalEmails: 'additionalEmails',
            additionalEmailsStr: 'additionalEmailsStr',
            unsubscribedEmails: 'unsubscribedEmails',
            unsubscribedEmailsStr: 'unsubscribedEmailsStr'
        },
        notifications: {
            pageId: 'notifications',
            lockedToPlan: 'lockedToPlan',
            notificationsMode: 'notificationsMode',
            notificationsLang: 'notificationsLang',
            notificationsLangValues: {
                english: 'ENGLISH',
                japanese: 'JAPANESE',
                hebrew: 'HEBREW',
                german: 'GERMAN'
            },
            infectedDevice: 'infectedDevice',
            fileBlocked: 'fileBlocked',
            fileDownloaded: 'fileDownloaded',
            emailReceived: 'emailReceived',
            emailBlocked: 'emailBlocked',
            deviceDetected: 'deviceDetected',
            deviceReconnected: 'deviceReconnected',
            primaryInternetRestored: 'primaryInternetRestored',
            primaryInternetDown: 'primaryInternetDown',
            gatewayUp: 'gatewayUp',
            unexpectedReboot: 'unexpectedReboot',
            newFirmware: 'newFirmware',
            licenseAboutToExpire: 'licenseAboutToExpire',
            licenseActivated: 'licenseActivated',
            licenseExpired: 'licenseExpired',
            hwEMMC: 'hwEmmcAboutToBeFull',
/*             vpnTunnelDown: 'vpnTunnelDown',
            vpnCertificateAboutToExpire: 'vpnCertificateAboutToExpire',
            vpnCertificateExpired: 'vpnCertificateExpired' */
        },
        notificationsRecipients: {
            pageId: 'notificationsRecipients',
            lockedToPlan: 'lockedToPlan',
            sendToOwner: 'sendToOwner',
            additionalEmails: 'additionalEmails',
            additionalEmailsStr: 'additionalEmailsStr',
            unsubscribedEmails: 'unsubscribedEmails',
            unsubscribedEmailsStr: 'unsubscribedEmailsStr'
        },
        dynamicDns: {
            pageId: 'dynamicDNS',
            primaryDomainName: 'primaryDomainName',
            dnsAliasList: 'dnsAliasList'
        },
        vpnCommunity: {
            pageId: 'vpnCommunity',
            lockedToPlan: 'lockedToPlan',
            communityDataList: 'communityDataList',
            communityDataSchema: {
                name: 'name',
                isCenter: 'isCenter'
            },
            noLocalConnsEncrypt: 'noLocalConnsEncrypt',
            useDnsName: 'useDnsName',
            dnsName: 'dnsName',
            defaultDNSName: 'defaultDNSName'
        },
        vpnAuthenticationMethod: {
            pageId: 'vpnAuthenticationMethod',
            vpnAuthMethod: 'vpnAuthMethod',
            vpnAuthMethodValues: {
                certificate: 'VPN_AUTH_CERTIFICATE',
                sharedSecret: 'VPN_AUTH_SHARED_SECRET'
            },
            certificateAuthority: 'certificateAuthority',
            internalCaValue: 'Internal_CA',
            externalCertSubject: 'externalCertSubject',
            fullDn: 'fullDn',
            sharedSecret: 'sharedSecret'
        },
        vpnInternalNetworkTopology: {
            pageId: 'vpnInternalNetworkTopology',
            topologyList: 'topologyList',
            excludeTopologyList: 'excludeTopologyList',
            topologySchema: {
                name: 'name',
                network: 'net',
                mask: 'mask'
            }
        },
        administratorAccess: {
            pageId: 'administratorAccess',
            lockedToPlan: 'lockedToPlan',
            adminAccessDataList: 'adminAccessDataList',
            adminAccessDataListSchema: {
                ipAddr: 'ipAddr',
                subnetMask: 'subnetMask',
                domainName: 'domainName',
                type: 'type',
                typeValues: {
                    ipAddress: 'IP Address',
                    network: 'Network'
                }
            }
        },
        gatewaysBehindNAT: {
            pageId: 'gatewaysBehindNAT',
            lockedToPlan: 'lockedToPlan',
            accessServiceUrl: 'accessServiceUrl',
            accessServiceMode: 'accessServiceMode',
            mgmtPort: 'mgmtPort',
            sicPort: 'sicPort'
        },
        gatewayBladesLicenses: {
            pageId: 'gatewayBladesLicenses',
            licenseItemList: 'licenseItemList',
            licenseItemListSchema: {
                name: 'name',
                expiration: 'expiration',
                service: 'service'
            }
        },
        cliScripts: {
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            script: 'script',
            topicOrder: 'topicOrder',
            scriptRunRC: 'scriptRunRC',
            scriptRunText: 'scriptRunText',
            scriptRunTimestamp: 'scriptRunTimestamp',
            scriptDownloadRC: 'scriptDownloadRC',
            scriptDownloadText: 'scriptDownloadText',
            scriptDownloadTimestamp: 'scriptDownloadTimestamp',
        },
        cliScript1: {
            pageId: 'cliScript1',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            script: 'script',
            topicOrder: 'topicOrder',
            scriptRunRC: 'scriptRunRC',
            scriptRunText: 'scriptRunText',
            scriptRunTimestamp: 'scriptRunTimestamp',
            scriptDownloadRC: 'scriptDownloadRC',
            scriptDownloadText: 'scriptDownloadText',
            scriptDownloadTimestamp: 'scriptDownloadTimestamp',
        },
        cliScript2: {
            pageId: 'cliScript2',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            script: 'script',
            topicOrder: 'topicOrder',
            scriptRunRC: 'scriptRunRC',
            scriptRunText: 'scriptRunText',
            scriptRunTimestamp: 'scriptRunTimestamp',
            scriptDownloadRC: 'scriptDownloadRC',
            scriptDownloadText: 'scriptDownloadText',
            scriptDownloadTimestamp: 'scriptDownloadTimestamp',
        },
        cliScript3: {
            pageId: 'cliScript3',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            script: 'script',
            topicOrder: 'topicOrder',
            scriptRunRC: 'scriptRunRC',
            scriptRunText: 'scriptRunText',
            scriptRunTimestamp: 'scriptRunTimestamp',
            scriptDownloadRC: 'scriptDownloadRC',
            scriptDownloadText: 'scriptDownloadText',
            scriptDownloadTimestamp: 'scriptDownloadTimestamp',
        },
        cliScript4: {
            pageId: 'cliScript4',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            script: 'script',
            topicOrder: 'topicOrder',
            scriptRunRC: 'scriptRunRC',
            scriptRunText: 'scriptRunText',
            scriptRunTimestamp: 'scriptRunTimestamp',
            scriptDownloadRC: 'scriptDownloadRC',
            scriptDownloadText: 'scriptDownloadText',
            scriptDownloadTimestamp: 'scriptDownloadTimestamp',
        },
        cliScript5: {
            pageId: 'cliScript5',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            script: 'script',
            topicOrder: 'topicOrder',
            scriptRunRC: 'scriptRunRC',
            scriptRunText: 'scriptRunText',
            scriptRunTimestamp: 'scriptRunTimestamp',
            scriptDownloadRC: 'scriptDownloadRC',
            scriptDownloadText: 'scriptDownloadText',
            scriptDownloadTimestamp: 'scriptDownloadTimestamp',
        },
        cliScriptFromApi: {
            pageId: 'cliScriptFromAPI',
            lockedToPlan: 'lockedToPlan',
            manageInSmp: 'manageInSmp',
            script: 'script',
            topicOrder: 'topicOrder',
            scriptRunRC: 'scriptRunRC',
            scriptRunText: 'scriptRunText',
            scriptRunTimestamp: 'scriptRunTimestamp',
            scriptDownloadRC: 'scriptDownloadRC',
            scriptDownloadText: 'scriptDownloadText',
            scriptDownloadTimestamp: 'scriptDownloadTimestamp',
        },
        haStatus: {
            pageId: 'haStatus',
            state: 'state',
            details: 'details',
            member1: 'member1',
            member2: 'member2',
            memberSchema: {
                name: 'name',
                state: 'state',
                stateValues: {
                    unknown: 'Unknown',
                    down: 'Down',
                    standby: 'Standby',
                    active: 'Active',
                    inactive: 'Inactive'
                },
                detailedState: 'detailedState',
                macAddress: 'macAddress',
                lastConnectedIPAddress: 'lastConnectedIPAddress',
                reportedFirmware: 'reportedFirmware',
                connectionStatus: 'connectionStatus',
                connectionStatusValues: {
                    connected: 'SMP.GATEWAYS.PAGE.STATUS.CONNECTED',
                    notConnected: 'SMP.GATEWAYS.PAGE.STATUS.NOT_CONNECTED',
                    disabled: 'SMP.GATEWAYS.PAGE.STATUS.DISABLED',
                }
            }
        },
        haMemberStatus: {
            pageId: 'haMemberStatus',
            id: 'id',
            isHa: 'isHa',
            state: 'state',
            stateValues: {
                unknown: 'Unknown',
                down: 'Down',
                standby: 'Standby',
                active: 'Active',
                inactive: 'Inactive'
            },
            detailedState: 'detailedState',
            hasBeenConnected: 'hasBeenConnected',
            lastChangeTime: 'lastChangeTime',
            pnotes: 'pnotes'
        },
        customFields: {
            pageId: 'customFields',
            customFieldOne: 'customField1',
            customFieldTwo: 'customField2',
            customFieldSchema: {
                name: 'name',
                value: 'value'
            }
        }
    },
    shared: {
        lockedToPlan: 'lockedToPlan',
    }
}

export const GatewayCreateSchema = {
    name: 'name',
    description: 'description',
    managed: 'managed',
    staticIP: 'staticIp',
    planId: 'planId',
    registrationKey: 'regKey',
    ownerId: 'ownerId'
};

export const ClusterGatewayCreateSchema = {
    name: 'name',
    description: 'description',
    managed: 'managed',
    staticIP: 'staticIp',
    planId: 'planId',
    ownerId: 'ownerId',
    member1: 'member1',
    member2: 'member2'
};

export const GatewayGetListSchema = {
    name: 'name',
    portalName: 'portalName',
    status: 'status',
    haStatus: 'haStatus',
    internetStatus: 'internetStatus',
    description: 'description',
    humanReadableAddress: 'humanReadableAddress',
    gatewayIp: 'gatewayIp',
    accessGatewayEnabled: 'accessGatewayEnabled',
    macAddress: 'macAddress',
    licenseGeneralStatus: 'licenseGeneralStatus',
    nearestExpirationDate: 'nearestExpirationDate',
    ownerId: 'ownerId',
    gatewayType: 'gatewayType',
    reportedFirmware: 'reportedFirmware',
    planId: 'planId',
    location: 'location',
    locationSchema: {
        lat: 'lat',
        lng: 'lng'
    },
        custom1: 'custom1', 
        custom2: 'custom2'
}

export const GatewayConnectedStatuses = {
    connected: 'connected',
    disconnected: 'disconnected',
    disabled: 'disabled'
}

export const GatewayHAStatuses = {
    ok: 'Ok',
    warning: 'Warning',
    error: 'Error'
}

export const GatewayFilterSchema = {
    freeTextSearch: 'freeTextSearch',
    connected: 'connected',
    disconnected: 'disconnected',
    disabled: 'disabled',
    mac: 'mac',
    macAddressList: 'macAddressList',
    plan: 'plan',
    vendor: 'vendor',
    vendorValues: {
        smallOfficeAppliance: 'Small Office Appliance',
        SmallOfficeHa: 'Small Office HA'
    },
    gatewayType: 'gatewayType',
    gatewayTypeValues: {
        // 600 Family
        appliance600: '600 Appliance',
        // 1200 Family
        appliance1200R: '1200R Appliance',
        // 700/1400 Family
        appliance730: '730 Appliance',
        appliance750: '750 Appliance',
        appliance790: '790 Appliance',
        appliance1430: '1430 Appliance',
        appliance1450: '1450 Appliance',
        appliance1470: '1470 Appliance',
        appliance1490: '1490 Appliance',
        // 1500 Family
        appliance1530: '1530 Appliance',
        appliance1550: '1550 Appliance',
        appliance1570: '1570 Appliance',
        appliance1590: '1590 Appliance',
        appliance1570R: '1570R Appliance',
        // 1600/1800 Family
        appliance1600: '1600 Appliance',
        appliance1800: '1800 Appliance'
    },
    reportedFirmware: 'reportedFirmware',
    firstConnection: 'firstConnection',
    lastConnection: 'lastConnection',
    creationDate: 'creationDate',
    modificationDate: 'modificationDate'
}

// const c = {
//   gateway: {
//     id: 'omri-gateway.omri-domain',
//     ownerId: 'omriha',
//     planId: 'Plan-3',
//     general: {
//         desc: 'Descript',
//         enabled: false,
//         gateway_type: '730 Appliance',
//         reported_firmware: 'R77.20.80_990172370',
//         managed_by_smp: false,
//         mac_addr: '00:1C:7F:95:E2:38',
//         last_conn_ip: '172.23.54.6',
//         reg_key: 'Ckrvc4bd',
//     },
//     location: {
//         desc: 'descLocationFromGw',
//     },
//     status: {
//         created: 'June 11, 2018 16:54:11',
//         last_modified: 'August 27, 2019 13:47:27',
//         connected: false,
//     },
//     ntp: {
//         lockedToPlan: true,
//         manageInSmp: false,
//         primary_ntp_server: 'ntp.checkpoint.com (gateway)',
//         secondary_ntp_server: 'ntp2.checkpoint.com (gateway)',
//         update_interval: '30',
//         ntp_authentication: true,
//         shared_secret: '123',
//         shared_secret_id: '123',
//     },
//     timezone: {
//         lockedToPlan: true,
//         manageInSmp: false,
//         local_time_zone: 'jerusalem',
//         daylight_savings: false,
//     },
//     dns: {
//         lockedToPlan: true,
//         manageInSmp: false,
//         desc: 'descDnsFromGw',
//     },
//     administrators: {
//         lockedToPlan: true,
//         manageInSmp: false,
//         desc: 'descAdministratorsFromGw',
//     }
// }
// }